import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useCurrentClinicId } from "@/hooks/useAppointments";

export interface TopItemRow {
  itemId: string | null;
  itemName: string;
  type: "PRODUTO" | "SERVICO" | "PACOTE";
  quantity: number;
  revenue: number;
}

export type AbcCurve = "A" | "B" | "C";

export interface TopOwnerRow {
  ownerId: string | null;
  ownerName: string;
  totalSpent: number;
  saleCount: number;
  // enriched after fetch
  speciesIds: string[];
  speciesNames: string[];
  breedIds: string[];
  breedNames: string[];
  // computed in UI
  abcCurve?: AbcCurve;
  cumulativePct?: number;
}

export interface ProductOutflowRow {
  itemId: string;
  itemName: string;
  quantity: number;
}

export interface ReportsFinancialData {
  revenueByService: number;
  revenueByProduct: number;
  totalRevenue: number;
  ticketMedio: number;
  topItems: TopItemRow[];
  topOwners: TopOwnerRow[];
  productOutflow: ProductOutflowRow[];
}

export function useReportsFinancial(dateFrom: Date | null, dateTo: Date | null) {
  const { data: clinicId } = useCurrentClinicId();

  return useQuery({
    queryKey: ["reports-financial", clinicId, dateFrom?.toISOString(), dateTo?.toISOString()],
    queryFn: async (): Promise<ReportsFinancialData> => {
      if (!clinicId || !dateFrom || !dateTo) {
        return {
          revenueByService: 0,
          revenueByProduct: 0,
          totalRevenue: 0,
          ticketMedio: 0,
          topItems: [],
          topOwners: [],
          productOutflow: [],
        };
      }

      const fromIso = dateFrom.toISOString();
      const toIso = dateTo.toISOString();

      const { data: sales, error: salesErr } = await supabase
        .from("sales")
        .select("id, total, owner_id")
        .eq("clinic_id", clinicId)
        .eq("status", "PAGA")
        .gte("created_at", fromIso)
        .lte("created_at", toIso);

      if (salesErr) throw salesErr;

      const saleIds = (sales ?? []).map((s) => s.id);
      let saleItems: { sale_id: string; item_id: string | null; item_name: string; line_total: number; quantity: number }[] = [];
      const itemTypes: Record<string, "PRODUTO" | "SERVICO" | "PACOTE"> = {};

      if (saleIds.length > 0) {
        const { data: itemsData, error: itemsErr } = await supabase
          .from("sale_items")
          .select("sale_id, item_id, item_name, line_total, quantity")
          .in("sale_id", saleIds);

        if (itemsErr) throw itemsErr;
        saleItems = (itemsData ?? []) as typeof saleItems;

        const itemIds = [...new Set(saleItems.map((i) => i.item_id).filter(Boolean))] as string[];
        if (itemIds.length > 0) {
          const { data: items } = await supabase.from("items").select("id, type").in("id", itemIds);
          (items ?? []).forEach((it: { id: string; type: string }) => {
            itemTypes[it.id] = it.type as "PRODUTO" | "SERVICO" | "PACOTE";
          });
        }
      }

      const totalRevenue = (sales ?? []).reduce((sum, s) => sum + Number(s.total ?? 0), 0);
      const revenueByService = saleItems
        .filter((si) => {
          const type = itemTypes[si.item_id ?? ""];
          return type === "SERVICO" || type === "PACOTE";
        })
        .reduce((sum, si) => sum + si.line_total, 0);
      const revenueByProduct = saleItems
        .filter((si) => itemTypes[si.item_id ?? ""] === "PRODUTO")
        .reduce((sum, si) => sum + si.line_total, 0);
      const ticketMedio = (sales ?? []).length > 0 ? totalRevenue / (sales ?? []).length : 0;

      const itemAgg: Record<string, { name: string; quantity: number; revenue: number; type: "PRODUTO" | "SERVICO" | "PACOTE" }> = {};
      saleItems.forEach((si) => {
        const id = si.item_id ?? "__null__";
        const type = (si.item_id ? itemTypes[si.item_id] : "PRODUTO") ?? "PRODUTO";
        if (!itemAgg[id]) itemAgg[id] = { name: si.item_name, quantity: 0, revenue: 0, type };
        itemAgg[id].quantity += si.quantity;
        itemAgg[id].revenue += si.line_total;
      });
      const topItems: TopItemRow[] = Object.entries(itemAgg).map(([itemId, v]) => ({
        itemId: itemId === "__null__" ? null : itemId,
        itemName: v.name,
        type: v.type,
        quantity: v.quantity,
        revenue: v.revenue,
      }));

      const ownerAgg: Record<string, { totalSpent: number; saleCount: number }> = {};
      (sales ?? []).forEach((s) => {
        const oid = s.owner_id ?? "__null__";
        if (!ownerAgg[oid]) ownerAgg[oid] = { totalSpent: 0, saleCount: 0 };
        ownerAgg[oid].totalSpent += Number(s.total ?? 0);
        ownerAgg[oid].saleCount += 1;
      });
      const ownerIds = Object.keys(ownerAgg).filter((k) => k !== "__null__");
      const ownerNames: Record<string, string> = {};
      const ownerSpeciesIds: Record<string, string[]> = {};
      const ownerSpeciesNames: Record<string, string[]> = {};
      const ownerBreedIds: Record<string, string[]> = {};
      const ownerBreedNames: Record<string, string[]> = {};

      if (ownerIds.length > 0) {
        const [ownersRes, petsRes] = await Promise.all([
          supabase.from("owners").select("id, name").in("id", ownerIds),
          supabase
            .from("pets")
            .select("owner_id, species_id, breed_id, species:species_id(id, name), breed:breed_id(id, name)")
            .in("owner_id", ownerIds)
            .is("deleted_at", null),
        ]);

        (ownersRes.data ?? []).forEach((o: { id: string; name: string }) => {
          ownerNames[o.id] = o.name ?? "Sem nome";
        });

        type PetRow = { owner_id: string; species_id: string | null; breed_id: string | null; species: { id: string; name: string } | null; breed: { id: string; name: string } | null };
        (petsRes.data ?? []).forEach((p: PetRow) => {
          const oid = p.owner_id;
          if (!oid) return;
          if (p.species_id && !ownerSpeciesIds[oid]?.includes(p.species_id)) {
            ownerSpeciesIds[oid] = [...(ownerSpeciesIds[oid] ?? []), p.species_id];
            ownerSpeciesNames[oid] = [...(ownerSpeciesNames[oid] ?? []), p.species?.name ?? ""];
          }
          if (p.breed_id && !ownerBreedIds[oid]?.includes(p.breed_id)) {
            ownerBreedIds[oid] = [...(ownerBreedIds[oid] ?? []), p.breed_id];
            ownerBreedNames[oid] = [...(ownerBreedNames[oid] ?? []), p.breed?.name ?? ""];
          }
        });
      }

      const topOwners: TopOwnerRow[] = Object.entries(ownerAgg).map(([ownerId, v]) => ({
        ownerId: ownerId === "__null__" ? null : ownerId,
        ownerName: ownerId === "__null__" ? "Não informado" : ownerNames[ownerId] ?? "Desconhecido",
        totalSpent: v.totalSpent,
        saleCount: v.saleCount,
        speciesIds: ownerSpeciesIds[ownerId] ?? [],
        speciesNames: ownerSpeciesNames[ownerId] ?? [],
        breedIds: ownerBreedIds[ownerId] ?? [],
        breedNames: ownerBreedNames[ownerId] ?? [],
      }));

      const { data: movements, error: movErr } = await supabase
        .from("product_stock_movements")
        .select("item_id, quantity, type, deleted_at")
        .eq("clinic_id", clinicId)
        .in("type", ["SAIDA", "CONSUMO_INTERNO", "DEVOLUCAO_CLIENTE"])
        .gte("created_at", fromIso)
        .lte("created_at", toIso);

      if (movErr) throw movErr;

      type MovementRow = {
        item_id: string;
        quantity: number;
        deleted_at?: string | null;
      };

      const activeMovements = (movements ?? []).filter(
        (m) =>
          typeof (m as MovementRow | undefined)?.item_id === "string" &&
          (m as MovementRow).deleted_at == null
      );
      const itemIdsMov = [...new Set(activeMovements.map((m) => m.item_id))];
      const itemNamesMov: Record<string, string> = {};
      if (itemIdsMov.length > 0) {
        const { data: itemsMov } = await supabase.from("items").select("id, name").in("id", itemIdsMov);
        (itemsMov ?? []).forEach((it: { id: string; name: string }) => {
          itemNamesMov[it.id] = it.name ?? "";
        });
      }
      const outflowAgg: Record<string, number> = {};
      activeMovements.forEach((m) => {
        outflowAgg[m.item_id] = (outflowAgg[m.item_id] ?? 0) + Math.abs(m.quantity);
      });
      const productOutflow: ProductOutflowRow[] = Object.entries(outflowAgg).map(([itemId, quantity]) => ({
        itemId,
        itemName: itemNamesMov[itemId] ?? "Item",
        quantity,
      }));

      topItems.sort((a, b) => b.revenue - a.revenue);
      topOwners.sort((a, b) => b.totalSpent - a.totalSpent);
      productOutflow.sort((a, b) => b.quantity - a.quantity);

      return {
        revenueByService,
        revenueByProduct,
        totalRevenue,
        ticketMedio,
        topItems,
        topOwners,
        productOutflow,
      };
    },
    enabled: !!clinicId && !!dateFrom && !!dateTo,
  });
}
